import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

/**
 * Personal 2-for-1 referral code card, shown on the user's own profile.
 * - Fetches /referral/me on mount.
 * - Locked state when the user hasn't paid for a real Pouls event yet.
 * - Copy-to-clipboard with a 2s "Copied" confirmation.
 */
export default function ReferralCodeCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/referral/me`, { headers });
        if (res.ok) {
          const d = await res.json();
          if (!cancelled) setData(d);
        }
      } catch {
        // silent — the section just won't render
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCopy = async () => {
    if (!data?.code) return;
    try {
      await navigator.clipboard.writeText(data.code);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Unable to copy — please copy manually");
    }
  };

  if (loading) return null; // No skeleton — this is a secondary panel
  if (!data?.code) return null;

  const eligible = !!data.eligible;

  return (
    <Card data-testid="referral-code-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-4 h-4 text-primary-ink" />
          <h3 className="text-sm font-semibold">Your 2-for-1 code</h3>
        </div>

        <div className={`flex items-center gap-2 rounded-lg border p-3 mb-3 ${eligible ? "border-primary/50 bg-primary/10" : "border-border bg-muted/40"}`}>
          <span
            className={`flex-1 font-mono tracking-widest text-lg font-bold ${eligible ? "text-primary-ink" : "text-muted-foreground"}`}
            data-testid="referral-code-value"
          >
            {data.code}
          </span>
          <Button
            variant={eligible ? "default" : "outline"}
            size="sm"
            onClick={handleCopy}
            disabled={!eligible}
            data-testid="referral-copy-btn"
          >
            {copied ? (
              <><Check className="w-3.5 h-3.5 mr-1" />Copied</>
            ) : (
              <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>
            )}
          </Button>
        </div>

        {eligible ? (
          <p className="text-xs text-muted-foreground leading-relaxed">
            Share this code with a friend. When they enter it at Stripe checkout for a paid Pouls event,
            they pay full price for their spot — and you get to <span className="text-foreground font-medium">bring them along for free</span>.
            One +1 per event.
            {data.used_count > 0 && (
              <> You&apos;ve invited <span className="text-foreground font-medium">{data.used_count}</span> friend{data.used_count !== 1 ? "s" : ""} so far. 🎉</>
            )}
          </p>
        ) : (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Your code unlocks after you pay for your first Pouls event. Come to a game, series, or trip and it&apos;ll go live automatically.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
